import React, { useContext, useEffect, useState } from "react";
import { Checkbox, Form, Input } from "antd";
import {
  cardioDiagnosisOptions,
  physioDiagnosisOptions,
  neuroDiagnosisOptions,
  orthoDiagnosisOptions,
  spinalCordOptions,
  sportsDiagnosisOptions,
  strokeOptions,
} from "../../Options/physioOptions";
import CheckListFBYInput from "../../../../blocks/FormItems/CheckListFBYInput";
import DynamicInputFields from "../../../../blocks/FormItems/DynamicInputFields";

const FunctionalDiagnosis = ({ context, form }) => {
  const { formData } = useContext(context);
  const [checkboxes, setCheckboxes] = useState({});
  const [showOthers, setShowOthers] = useState({});
  const [showSubOptions, setShowSubOptions] = useState({});
  const [showSubSubOptions, setShowSubSubOptions] = useState({});
  const [sportCheckboxes, setSportCheckboxes] = useState({});
  const [orthoCheckboxes, setOrthoCheckboxes] = useState({});

  const getOptions = (category) => {
    const optionsMap = {
      cardio: cardioDiagnosisOptions,
      ortho: orthoDiagnosisOptions,
      sports: sportsDiagnosisOptions,
      neuro: neuroDiagnosisOptions,
    };
    return optionsMap[category] || [];
  };

  const getSubOptions = (condition) => {
    if (["stroke", "head_injury"].includes(condition)) return strokeOptions;
    if (condition === "spinal_cord_injury") return spinalCordOptions;
    return [];
  };

  const handleCheckboxChange = (value, checked) => {
    setCheckboxes((prev) => ({
      ...prev,
      [value]: checked,
    }));
    form?.setFieldValue?.(value, checked);

    if (value === "others" && !checked) {
      form?.setFieldValue?.("others", []);
    }
  };

  const handleSubCheckboxChange = (
    checkedValue,
    checked,
    mainCategory,
    subOption
  ) => {
    const isOthers = subOption.value === "others";

    form?.setFieldValue?.(`${mainCategory}_${subOption.value}`, checked);

    if (isOthers) {
      setShowOthers((prev) => ({
        ...prev,
        [mainCategory]: checked,
      }));
      if (!checked) {
        form?.setFieldValue?.(`${mainCategory}_others`, []);
      }
    }

    if (
      ["stroke", "head_injury", "spinal_cord_injury"].includes(subOption.value)
    ) {
      setShowSubOptions((prev) => ({
        ...prev,
        [subOption.value]: checked,
      }));

      if (!checked) {
        form?.setFieldValue?.(`${mainCategory}_${subOption.value}_options`, []);
        form?.setFieldValue?.(
          `${mainCategory}_${subOption.value}_description`,
          ""
        );
        setShowSubSubOptions((prev) => ({
          ...prev,
          [subOption.value]: false,
        }));
      }
    }
  };

  const handleSubSubCheckboxChange = (
    checkedValues,
    mainCategory,
    subOption
  ) => {
    const key = `${mainCategory}_${subOption.value}`;

    // Update the state for "others" key based on the selected values
    setShowSubSubOptions((prev) => ({
      ...prev,
      [subOption.value]: checkedValues.includes("others"), // true if "others" is selected
    }));

    if (!checkedValues.includes("others")) {
      form?.setFieldValue?.(`${key}_description`, "");
    }
  };

  useEffect(() => {
    if (form?.isFieldsTouched?.()) return;

    const initialCheckboxes = physioDiagnosisOptions?.reduce((acc, option) => {
      acc[option.value] =
        formData?.functional_diagnosis?.[option.value] || false;
      return acc;
    }, {});
    setCheckboxes(initialCheckboxes);

    const hasOthersContent = (others) =>
      Array.isArray(others) &&
      others.some((item) => {
        const remarks = item?.remarks ?? item;
        return typeof remarks === "string"
          ? remarks.trim() !== ""
          : remarks != null && remarks !== "";
      });

    const initialShowOthers = physioDiagnosisOptions?.reduce((acc, option) => {
      const others =
        formData?.functional_diagnosis?.[`${option.value}_others`];
      acc[option.value] = hasOthersContent(others);
      return acc;
    }, {});
    setShowOthers(initialShowOthers);

    const subOptions = ["stroke", "head_injury", "spinal_cord_injury"].reduce(
      (acc, key) => {
        acc[key] = !!formData?.functional_diagnosis?.[`neuro_${key}`];
        return acc;
      },
      {}
    );
    setShowSubOptions(subOptions);

    const subSubOptions = [
      "stroke",
      "head_injury",
      "spinal_cord_injury",
    ].reduce((acc, key) => {
      acc[key] =
        !!formData?.functional_diagnosis?.[`neuro_${key}_options`]?.includes(
          "others"
        );
      return acc;
    }, {});
    setShowSubSubOptions(subSubOptions);

    const orthoChecked = orthoDiagnosisOptions?.reduce((acc, checkbox) => {
      acc[checkbox.value] =
        formData?.functional_diagnosis?.ortho?.[checkbox.value];
      return acc;
    }, {});
    setOrthoCheckboxes(orthoChecked);
    const sportsChecked = sportsDiagnosisOptions?.reduce((acc, checkbox) => {
      acc[checkbox.value] =
        formData?.functional_diagnosis?.sports?.[checkbox.value];
      return acc;
    }, {});
    setSportCheckboxes(sportsChecked);
  }, [formData, form]);

  return (
    <>
      {physioDiagnosisOptions.map((mainOption) => (
        <React.Fragment key={mainOption.value}>
          <Form.Item name={mainOption.value} valuePropName="checked">
            <Checkbox
              checked={checkboxes[mainOption.value]}
              onChange={(e) =>
                handleCheckboxChange(mainOption.value, e.target.checked)
              }
            >
              {mainOption.label}
            </Checkbox>
          </Form.Item>

          {checkboxes[mainOption?.value] && mainOption?.value === "others" ? (
            <>
              <DynamicInputFields
                field_key={"others"}
                form={form}
                inputStyle={{ maxWidth: 800 }}
              />
            </>
          ) : mainOption?.value === "sports" &&
            checkboxes[mainOption?.value] ? (
            <div className="subsection-card">
              <CheckListFBYInput
                options={sportsDiagnosisOptions}
                checkboxes={sportCheckboxes}
                setCheckboxes={setSportCheckboxes}
                keyChain={"sports"}
                others_key={"others"}
                form={form}
                field_key={["sports", "others"]}
              />
            </div>
          ) : mainOption?.value === "ortho" && checkboxes[mainOption?.value] ? (
            <div className="subsection-card">
              <CheckListFBYInput
                options={orthoDiagnosisOptions}
                checkboxes={orthoCheckboxes}
                setCheckboxes={setOrthoCheckboxes}
                keyChain={"ortho"}
                others_key={"others"}
                form={form}
                field_key={["ortho", "others"]}
              />
            </div>
          ) : (
            checkboxes[mainOption?.value] && (
              <div className="subsection-card">
                {getOptions(mainOption.value).map((subOption) => (
                  <div key={subOption.value}>
                    <Form.Item
                      name={`${mainOption.value}_${subOption.value}`}
                      valuePropName="checked"
                    >
                      <Checkbox
                        onChange={(e) =>
                          handleSubCheckboxChange(
                            subOption.value,
                            e.target.checked,
                            mainOption.value,
                            subOption
                          )
                        }
                      >
                        {subOption.label}
                      </Checkbox>
                    </Form.Item>

                    {["stroke", "head_injury", "spinal_cord_injury"]?.includes(
                      subOption.value
                    ) &&
                      showSubOptions[subOption.value] && (
                        <div className="subsection-card">
                          <Form.Item
                            name={`${mainOption.value}_${subOption.value}_options`}
                            // valuePropName="checked"
                          >
                            <Checkbox.Group
                              onChange={(checkedValues) =>
                                handleSubSubCheckboxChange(
                                  checkedValues,
                                  mainOption.value,
                                  subOption
                                )
                              }
                            >
                              {getSubOptions(subOption.value).map(
                                (thirdOption) => (
                                  <Checkbox value={thirdOption.value}>
                                    {thirdOption.label}
                                  </Checkbox>
                                )
                              )}
                            </Checkbox.Group>
                          </Form.Item>

                          {showSubSubOptions[`${subOption.value}`] && (
                            <Form.Item
                              name={`${mainOption.value}_${subOption.value}_description`}
                            >
                              <Input placeholder="Please enter the description" />
                            </Form.Item>
                          )}
                        </div>
                      )}
                  </div>
                ))}

                {showOthers[mainOption.value] && (
                  <>
                    <DynamicInputFields
                      field_key={`${mainOption.value}_others`}
                      form={form}
                      inputStyle={{ maxWidth: 800 }}
                    />
                  </>
                )}
              </div>
            )
          )}
        </React.Fragment>
      ))}
    </>
  );
};

export default FunctionalDiagnosis;
